import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TIER_THEMES, TIER_DESCRIPTION } from "@/lib/themes";
import { IdCard, Film, ShoppingCart, MapPin, Video, CreditCard, ArrowRight, Activity, ShieldCheck, Handshake } from "lucide-react";
import ProductionGovernanceCard from "@/components/ProductionGovernanceCard";
import OrgTeamPanel from "@/components/OrgTeamPanel";
import ProducersPanel from "@/components/ProducersPanel";
import PublicProfileCard from "@/components/PublicProfileCard";
import InvestorSlatePanel from "@/components/investor/InvestorSlatePanel";
import AffiliationBadges from "@/components/AffiliationBadges";
import HeroImageGenerator from "@/components/HeroImageGenerator";

const SPONSOR_ADMIN_TYPES = ["pr_agency_admin", "casting_agency_admin"];
const SPONSOR_KIND_LABEL = {
  pr_agency_admin:      "Journalists · Influencers · Paparazzi",
  casting_agency_admin: "Talent · Models",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ projects: 0, auditions: 0, attendance: 0 });
  const [org, setOrg]     = useState(null);
  const [pendingCount, setPendingCount] = useState(null);
  const [affBadges, setAffBadges] = useState([]);

  const isPH = user?.account_type === "production_house";
  const isInvestor = user?.account_type === "investor";
  const isSponsorAdmin = SPONSOR_ADMIN_TYPES.includes(user?.account_type);
  const isAdmin = Boolean(
    user?.is_admin ||
    (user?.email && user?.email === "admin@callsheet.app")
  );
  const [pendingOrgCount, setPendingOrgCount] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, a, at] = await Promise.all([
        api.get("/projects").then(r => r.data).catch(() => []),
        api.get("/auditions").then(r => r.data).catch(() => []),
        api.get("/attendance").then(r => r.data).catch(() => []),
      ]);
      setStats({ projects: p.length, auditions: a.length, attendance: at.length });
      // Section affiliation badges (JAA / TFJAA / CINTAA etc.) for header display.
      api.get("/icard")
         .then(r => setAffBadges(r.data.affiliation_badges || []))
         .catch(() => setAffBadges([]));
      if (isPH) {
        api.get("/onboard/me/organization")
           .then(r => setOrg(r.data.organization))
           .catch((err) => console.warn("Org fetch failed", err));
      }
      if (isSponsorAdmin) {
        api.get("/pending-individuals/mine")
           .then(r => setPendingCount(r.data.count ?? 0))
           .catch((err) => console.warn("Pending queue count failed", err));
      }
      if (isAdmin) {
        api.get("/admin/pending-orgs")
           .then(r => setPendingOrgCount(r.data.count ?? 0))
           .catch((err) => console.warn("Ghost org queue count failed", err));
      }
    })();
  }, [isPH, isSponsorAdmin, isAdmin]);

  const tier = TIER_THEMES[user?.tier];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">
      {/* Hello strip */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
            {isPH ? "Production House · Console" : "Today · Production Console"}
          </div>
          {isPH ? (
            <>
              <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
                  data-testid="dashboard-ph-name">
                <span style={{ color: "var(--association-theme)" }}>
                  {org?.brand_name || org?.legal_name || user?.name || "Production House"}
                </span>
              </h1>
              <p className="mt-2 text-sm text-white/60 max-w-xl">
                {org
                  ? <>Administered by <span className="text-white/85">{user?.name}</span></>
                  : <>Platform admin console.</>}
                {org?.legal_name && org?.brand_name && org.legal_name !== org.brand_name && (
                  <> · <span className="text-white/45">{org.legal_name}</span></>
                )}
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
                Welcome, <span style={{ color: "var(--association-theme)" }}>{user?.name}</span>
              </h1>
              <p className="mt-3 text-sm text-white/60 max-w-xl">
                {TIER_DESCRIPTION[user?.tier] || ""}
              </p>
            </>
          )}
        </div>
        <div className="flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/5"
             data-testid="dashboard-tier-badge">
          <span className="w-2 h-2 rounded-full" style={{ background: tier?.color, boxShadow: `0 0 12px ${tier?.color}` }} />
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/70">{tier?.label}</span>
        </div>
      </div>

      {/* Section affiliations — JAA / TFJAA / CINTAA / etc. */}
      {affBadges.length > 0 && (
        <div className="mb-8" data-testid="dashboard-affiliations">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">
            Industry Affiliations
          </div>
          <AffiliationBadges items={affBadges} testidPrefix="dashboard-aff-badge" />
        </div>
      )}

      {/* Production House Hero Image (Nano Banana) */}
      {isPH && org && (
        <div className="cs-card cs-grain rounded-2xl overflow-hidden mb-8"
             data-testid="ph-hero-card">
          {org.public_hero_image_url ? (
            <div className="relative">
              <img src={org.public_hero_image_url} alt="Production House hero"
                   className="w-full h-64 object-cover"
                   onError={(e) => { e.currentTarget.style.display = "none"; }}/>
              <div className="absolute bottom-3 right-3">
                <HeroImageGenerator
                  currentUrl={org.public_hero_image_url}
                  onGenerated={(url) => setOrg({ ...org, public_hero_image_url: url })} />
              </div>
            </div>
          ) : (
            <div className="p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5B82E]">
                  Public Profile · Hero Image
                </div>
                <div className="font-display text-2xl uppercase tracking-tight mt-1.5">
                  Give your studio a face
                </div>
                <div className="text-xs text-white/60 mt-2 max-w-xl">
                  Generate a cinematic hero image for your public production-house profile in
                  seconds. Three keywords is all you need — Nano Banana handles the rest.
                </div>
              </div>
              <HeroImageGenerator
                currentUrl=""
                onGenerated={(url) => setOrg({ ...org, public_hero_image_url: url })} />
            </div>
          )}
        </div>
      )}

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="Projects" value={stats.projects} icon={Film} />
        <StatCard label="Auditions" value={stats.auditions} icon={Video} />
        <StatCard label="Attendance Pings" value={stats.attendance} icon={Activity} />
        <StatCard label="Plan" value={user?.subscription_plan || "—"} icon={CreditCard} small />
      </div>

      {/* Admin — Ghost Org verification queue */}
      {isAdmin && (
        <Link
          to="/admin/pending-orgs"
          data-testid="admin-ghost-orgs-card"
          className="cs-card rounded-2xl p-6 mb-4 flex items-center gap-5 hover:translate-y-[-2px] transition-all border border-amber-500/30 bg-amber-500/[0.04]"
        >
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-500/15 text-amber-300 shrink-0">
            <ShieldCheck size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/80">
              Admin · Verification Queue
            </div>
            <div className="font-display text-2xl uppercase tracking-tight mt-1">
              Ghost Organizations
            </div>
            <div className="text-xs text-white/60 mt-1.5">
              {pendingOrgCount === null
                ? "Loading…"
                : pendingOrgCount === 0
                ? "Nothing pending. Auto-created orgs from staff signup will land here."
                : `${pendingOrgCount} ghost org(s) awaiting approval.`}
            </div>
          </div>
          {pendingOrgCount > 0 && (
            <div className="px-3 py-1.5 rounded-full bg-amber-400 text-black text-xs font-bold"
                 data-testid="admin-ghost-orgs-count">
              {pendingOrgCount}
            </div>
          )}
          <ArrowRight size={16} className="text-white/60 shrink-0" />
        </Link>
      )}

      {/* Sponsor Admin (PR / Casting agency) — pending verifications queue */}
      {isSponsorAdmin && (
        <Link
          to="/pending-individuals"
          data-testid="sponsor-pending-card"
          className="cs-card rounded-2xl p-6 mb-10 flex items-center gap-5 hover:translate-y-[-2px] transition-all border border-emerald-500/25 bg-emerald-500/[0.04]"
        >
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-500/15 text-emerald-300 shrink-0">
            <ShieldCheck size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300/80">
              Verification Queue
            </div>
            <div className="font-display text-2xl uppercase tracking-tight mt-1">
              Pending {SPONSOR_KIND_LABEL[user.account_type] || "Individuals"}
            </div>
            <div className="text-xs text-white/60 mt-1.5">
              {pendingCount === null
                ? "Loading…"
                : pendingCount === 0
                ? "Nothing pending. When individuals nominate you as sponsor, they'll appear here."
                : `${pendingCount} pending — approve or reject to activate their accounts.`}
            </div>
          </div>
          {pendingCount > 0 && (
            <div className="px-3 py-1.5 rounded-full bg-emerald-500 text-black text-xs font-bold"
                 data-testid="sponsor-pending-count">
              {pendingCount}
            </div>
          )}
          <ArrowRight size={16} className="text-white/60 shrink-0" />
        </Link>
      )}


      {/* Tile nav */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Tile to="/icard" icon={IdCard} title="Digital iCard"
          desc="Your verified, co-branded identity card." tid="tile-icard" />
        <Tile to="/projects" icon={Film} title="Projects"
          desc="Create projects, manage crew, enforce caps." tid="tile-projects" />
        <Tile to="/procurement" icon={ShoppingCart} title="Procurement Gate"
          desc="HOD orders frozen → LP trim & approve." tid="tile-procurement" />
        <Tile to="/attendance" icon={MapPin} title="Geo-Attendance"
          desc="Privacy-first. Active only between call & wrap." tid="tile-attendance" />
        <Tile to="/auditions" icon={Video} title="Auditions Vault"
          desc="Headless upload routing. Masked player." tid="tile-auditions" />
        <Tile to="/pricing" icon={CreditCard} title="Plans & Caps"
          desc="20 / 80 / 250 crew. Vendor catalog." tid="tile-pricing" />
        {isPH && (
          <Tile to="/producer/sales-deals" icon={Handshake} title="Sales Ledger"
            desc="Read-only mirror of every rights deal a sales agent has closed on your slate."
            tid="tile-producer-sales-deals" />
        )}
        {isPH && (
          <Tile to="/producer/screeners" icon={Video} title="Screener Room"
            desc="Preview URL library. Linked sales agents attach screeners to specific deals."
            tid="tile-producer-screeners" />
        )}
      </div>

      {/* Production House — governance + producer roster + team */}
      {isPH && org && (
        <div className="mt-12 space-y-8">
          <ProductionGovernanceCard org={org} />
          <ProducersPanel org={org} isAdmin={user?.id === org?.owner_id || isAdmin} />
          <OrgTeamPanel org={org} />
        </div>
      )}

      {/* Everyone gets the Public Industry Profile — the "office window" */}
      <div className="mt-12">
        <PublicProfileCard user={user} />
      </div>

      {/* Investor — LP-style slate view */}
      {isInvestor && (
        <div className="mt-12">
          <InvestorSlatePanel />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, small }) {
  return (
    <div className="cs-card rounded-xl p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-white/5">
        <Icon size={18} style={{ color: "var(--association-theme)" }} />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{label}</div>
        <div className={`font-display ${small ? "text-base" : "text-2xl"} uppercase tracking-tight`}>{value}</div>
      </div>
    </div>
  );
}

function Tile({ to, icon: Icon, title, desc, tid }) {
  return (
    <Link to={to} data-testid={tid}
      className="cs-card rounded-2xl p-6 group hover:translate-y-[-2px] transition-all">
      <div className="flex items-center gap-3 mb-4">
        <Icon size={22} style={{ color: "var(--association-theme)" }} />
        <h3 className="font-display text-xl uppercase tracking-tight">{title}</h3>
      </div>
      <p className="text-sm text-white/55 leading-relaxed">{desc}</p>
      <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] text-white/70 group-hover:text-white">
        Open <ArrowRight size={12} />
      </div>
    </Link>
  );
}
